import { HTMLProps, MouseEvent, ReactElement, RefObject } from 'react';

import { Trans, TransProps, useTranslation } from 'react-i18next';

import styles from './translated-text.module.scss';
import visIdStyles from './translated-text-visid.module.scss';
import { NamespacesI18n } from '@storefront/i18n';

export interface TranslatedTextProps extends TransProps<any> {
  links?: Record<string, HTMLProps<HTMLAnchorElement> & { href: string }>;
  underline?: boolean;
  linkStyle?: 'black' | 'blue' | 'white' | 'electric-blue';
  i18nNamespace?: NamespacesI18n;
  onLinkClick?: (
    event: MouseEvent<HTMLAnchorElement>,
    linkRef: RefObject<HTMLAnchorElement>
  ) => void;
}

interface ExternalLinkInterface
  extends Pick<TranslatedTextProps, 'linkStyle' | 'onLinkClick'> {
  link: HTMLProps<HTMLAnchorElement>;
  underline?: boolean;
}

export function ExternalLink({
  link,
  linkStyle = 'blue',
  underline,
}: Readonly<ExternalLinkInterface>) {
  return (
    <a
      id={link.id}
      data-testid={'translated-anchor'}
      className={
        (linkStyle === 'electric-blue' ? visIdStyles : styles)[
          linkStyle + '-disclaimer-link' + (underline ? '' : '-no-underline')
        ]
      }
      target="_blank"
      rel="noopener noreferrer"
      aria-label={link.title || 'External link'}
      {...link}
    />
  );
}

const handleTagLinks = ({
  links,
  underline,
  linkStyle,
  onLinkClick,
}: TranslatedTextProps) => {
  if (!links) {
    return {};
  }

  const finalComponent: Record<string, ReactElement> = {};

  Object.entries(links).forEach(([key, link]) => {
    finalComponent[key] = ExternalLink({
      link,
      underline,
      linkStyle,
      onLinkClick,
    });
  });

  return finalComponent;
};

export function TranslatedText({
  links,
  linkStyle = 'blue',
  underline = true,
  onLinkClick,
  components,
  i18nNamespace,
  ...props
}: Readonly<TranslatedTextProps>) {
  const namespace = i18nNamespace || NamespacesI18n.COMMON;
  const { t } = useTranslation(namespace);
  const commonComponents = {
    br: <br />,
    ul: <ul className={styles['dotted-list']} />,
    li: <li />,
  };

  return (
    <Trans
      t={t}
      components={{
        ...components,
        ...commonComponents,
        ...handleTagLinks({ links, underline, linkStyle, onLinkClick }),
      }}
      {...props}
    />
  );
}

export default TranslatedText;
